import { isAfter } from 'date-fns';
import { groupBy, sumBy } from 'lodash';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';
import {
  AggregatedSaleMetric,
  AggregateMetricsTimePeriod,
  Marketplace,
  Sale,
} from 'src/WebApiController';

import { SaleChartData } from '../DataInsightsContext';
import { formatByGranularity } from './groupSalesByGranularity';

type TempSaleChartData = {
  dateTime: string;
  totalPnl: number;
  totalRevenue: number;
  totalQuantity: number;
  marketshareQuantity: Record<string, number>;
};

export const getMarketplaceIdFromMarketplace = (
  marketplace: Marketplace | null
) => {
  if (!marketplace) return 'Other';
  let marketplaceId = 0;
  Object.values(Marketplace).forEach((mp, index) => {
    if (mp === marketplace) {
      marketplaceId = index + 1;
    }
  });
  return marketplaceId;
};

export const emptyMarketplaceData: Record<string, number> = Object.values(
  Marketplace
).reduce(
  (acc, marketplace, index) => {
    acc[index + 1] = 0;
    return acc;
  },
  {} as Record<string, number>
);

export const stitchChartData = (
  aggregatedHistoricalData: AggregatedSaleMetric[] | null | undefined,
  recentSalesData: Sale[] | null | undefined,
  timeZone: string,
  timePeriod: AggregateMetricsTimePeriod,
  useDecreasedGranularity: boolean
): SaleChartData[] | null => {
  if (
    !aggregatedHistoricalData ||
    !recentSalesData ||
    aggregatedHistoricalData.length < 1
  )
    return null;
  // Get the latest maxSaleTimestamp from aggregatedHistoricalData
  const latestMaxSaleTimestamp = aggregatedHistoricalData.reduce(
    (latest, row) => {
      const dt = new Date(row.maxSaleTimestamp);
      return dt > latest ? dt : latest;
    },
    new Date(0)
  );

  const allRecentSales: Sale[] = recentSalesData;

  // Filter recent sales to only include those after the latest maxSaleTimestamp
  const filteredRecentSales = allRecentSales.filter((sale) =>
    isAfter(new Date(stringToUtcDate(sale.saleDate)), latestMaxSaleTimestamp)
  );

  // Group the data by granularity
  const byAggregatedHistoricalDataGranularity = groupBy(
    aggregatedHistoricalData,
    (row) =>
      formatByGranularity(
        row.dateTime,
        timePeriod,
        timeZone,
        useDecreasedGranularity
      )
  ); // Local timezone

  const byRecentSalesGranularity = groupBy(filteredRecentSales, (row) =>
    formatByGranularity(
      row.saleDate,
      timePeriod,
      timeZone,
      useDecreasedGranularity
    )
  ); // Local timezone

  // Convert to dictionary keyed by dateTime
  const mergedByDateTime: Record<string, TempSaleChartData> = Object.entries(
    byAggregatedHistoricalDataGranularity
  ).reduce(
    (acc, [hour, rows]) => {
      const totalQuantity = sumBy(rows, (r) => r.totalQuantity);
      const quantityByMarketplace: Record<string, number> = {
        ...emptyMarketplaceData,
      };
      rows.forEach((row) => {
        const marketplaceId = row.marketplaceId;
        if (!quantityByMarketplace[marketplaceId]) {
          quantityByMarketplace[marketplaceId] = 0;
        }
        quantityByMarketplace[marketplaceId] += row.totalQuantity;
      });

      acc[hour] = {
        dateTime: hour,
        totalRevenue: sumBy(rows, (r) => r.totalRevenue),
        totalPnl: sumBy(rows, (r) => r.totalPnL),
        totalQuantity: totalQuantity,
        marketshareQuantity: quantityByMarketplace,
      };
      return acc;
    },
    {} as Record<string, TempSaleChartData>
  );

  let recentTotalRevenue = 0;
  let recentTotalPnl = 0;
  let recentTotalQuantity = 0;
  let recentMarketshare: Record<string, number> = {};

  // Merge recent sales data into the existing data
  Object.entries(byRecentSalesGranularity)
    .toSorted(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .forEach(([hour, sales]) => {
      const totalRevenue = sumBy(sales, (s) => s.ttlNetProcs?.amt ?? 0);
      const totalPnl = sumBy(sales, (s) => s.pandL?.amt ?? 0);
      const totalQuantity = sumBy(sales, (s) => s.qtySold);
      const recentMarketshareQuantity: Record<string, number> = {
        ...emptyMarketplaceData,
      };

      sales.forEach((sale) => {
        const marketplaceId = getMarketplaceIdFromMarketplace(sale.mkp);
        if (!recentMarketshareQuantity[marketplaceId]) {
          recentMarketshareQuantity[marketplaceId] = 0;
        }
        recentMarketshareQuantity[marketplaceId] += sale.qtySold;
      });

      if (!mergedByDateTime[hour]) {
        recentTotalRevenue += totalRevenue;
        recentTotalPnl += totalPnl;
        recentTotalQuantity += totalQuantity;

        mergedByDateTime[hour] = {
          dateTime: hour,
          totalRevenue: recentTotalRevenue,
          totalPnl: recentTotalPnl,
          totalQuantity: recentTotalQuantity,
          marketshareQuantity: recentMarketshareQuantity,
        };
      } else {
        const combinedMarketshare: Record<string, number> = {
          ...mergedByDateTime[hour].marketshareQuantity,
        };

        for (const [key, value] of Object.entries(recentMarketshare)) {
          combinedMarketshare[key] = (combinedMarketshare[key] ?? 0) + value;
        }

        mergedByDateTime[hour].totalRevenue += totalRevenue;
        mergedByDateTime[hour].totalPnl += totalPnl;
        mergedByDateTime[hour].totalQuantity += totalQuantity;
        mergedByDateTime[hour].marketshareQuantity = combinedMarketshare;

        recentTotalRevenue = mergedByDateTime[hour].totalRevenue;
        recentTotalPnl = mergedByDateTime[hour].totalPnl;
        recentTotalQuantity = mergedByDateTime[hour].totalQuantity;
        recentMarketshare = mergedByDateTime[hour].marketshareQuantity;
      }
    });

  // Calculate cumulative totals
  const cumulativeData = Object.values(mergedByDateTime).reduce<
    SaleChartData[]
  >((acc, row) => {
    const previousTotal = {
      netProceeds: acc.length > 0 ? acc[acc.length - 1].totalRevenue : 0,
      pnl: acc.length > 0 ? acc[acc.length - 1].totalPnl : 0,
      quantity: acc.length > 0 ? acc[acc.length - 1].totalQuantity : 0,
      marketshareQuantity:
        acc.length > 0 ? acc[acc.length - 1].marketshareQuantity : {},
    };

    const cumulativeMarketshare: Record<string, number> = {
      ...previousTotal.marketshareQuantity,
    };
    for (const [key, value] of Object.entries(row.marketshareQuantity)) {
      cumulativeMarketshare[key] = (cumulativeMarketshare[key] ?? 0) + value;
    }

    const newTotal = {
      netProceeds: (previousTotal.netProceeds || 0) + row.totalRevenue,
      cumulativePnl: (previousTotal.pnl || 0) + row.totalPnl,
      cumulativeQuantity: (previousTotal.quantity || 0) + row.totalQuantity,
      cumulativeMarketshare,
    };

    const rawMarketsharePercentages = Object.entries(
      newTotal.cumulativeMarketshare
    ).reduce<Record<string, number>>(
      (marketshareAcc, [marketplaceId, quantity]) => {
        const marketsharePercentage =
          newTotal.cumulativeQuantity > 0
            ? parseFloat((quantity / newTotal.cumulativeQuantity).toFixed(2))
            : 0;

        marketshareAcc[marketplaceId] = marketsharePercentage;
        return marketshareAcc;
      },
      {}
    );

    acc.push({
      ...row,
      totalRevenue: newTotal.netProceeds,
      totalPnl: newTotal.cumulativePnl,
      totalQuantity: newTotal.cumulativeQuantity,
      marketshareQuantity: newTotal.cumulativeMarketshare,
      marketshare: rawMarketsharePercentages,
    });

    return acc;
  }, []);

  return cumulativeData;
};
